import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="text-lg font-semibold text-foreground-900 tracking-tight whitespace-nowrap hover:text-primary-600 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              CSSSpreadsheet<span className="text-primary-500">.</span>lifestyle
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                Home
              </Link>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Categories
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-background-50 border border-background-200/80 rounded-lg py-2 shadow-lg animate-fade-in">
                    <div className="max-h-80 overflow-y-auto">
                      {categories.map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                          onClick={() => setDropdownOpen(false)}
                        >
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/what-is-cssbuy-spreadsheet"
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                Shopping Guide
              </Link>

              <Link
                to="/kakobuy-spreadsheet-guide"
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                Agent Guide
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                CSSBuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 border-2 border-foreground-900 text-foreground-900 text-sm font-medium rounded-full hover:bg-foreground-900 hover:text-background-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Menu"
              >
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ===== MOBILE MENU OVERLAY ===== */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          <div className="absolute top-16 left-0 right-0 bg-background-50 border-b border-background-200/70 shadow-lg animate-slide-down max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-4 py-4 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                <i className="ri-home-4-line text-lg"></i>
                Home
              </Link>

              <Link
                to="/what-is-cssbuy-spreadsheet"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                <i className="ri-book-open-line text-lg"></i>
                Shopping Guide
              </Link>

              <Link
                to="/kakobuy-spreadsheet-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                <i className="ri-guide-line text-lg"></i>
                Agent Guide
              </Link>

              <Link
                to="/agent-comparison-2026"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                <i className="ri-scales-3-line text-lg"></i>
                Agent Comparison
              </Link>

              <div className="border-t border-background-200 my-2"></div>

              <p className="px-3 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                Product Categories
              </p>

              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>

              <div className="border-t border-background-200 my-2"></div>

              <div className="flex flex-col gap-2 px-3 pt-2 pb-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  CSSBuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-foreground-900 text-foreground-900 text-sm font-medium rounded-full hover:bg-foreground-900 hover:text-background-50 transition-all cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}